/** Presentation helpers: relative ages, exact timestamps, durations, counts. */

const RELATIVE_STEPS: [limitSeconds: number, divisor: number, unit: string][] = [
  [60, 1, 's'],
  [3600, 60, 'm'],
  [86_400, 3600, 'h'],
  [2_592_000, 86_400, 'd'],
  [31_536_000, 2_592_000, 'mo'],
  [Number.POSITIVE_INFINITY, 31_536_000, 'y'],
];

/** Compact age such as `4m`, `3h`, `12d`; `now` under five seconds. */
export function relativeAge(value: string | Date | null | undefined, now = Date.now()): string {
  const ms = toMillis(value);
  if (ms === null) return '—';
  const seconds = Math.max(0, Math.round((now - ms) / 1000));
  if (seconds < 5) return 'now';
  for (const [limit, divisor, unit] of RELATIVE_STEPS) {
    if (seconds < limit) return `${Math.floor(seconds / divisor)}${unit}`;
  }
  return '—';
}

/** Signed relative label used for future times (`in 2h`) and past ones (`2h ago`). */
export function relativeLabel(value: string | Date | null | undefined, now = Date.now()): string {
  const ms = toMillis(value);
  if (ms === null) return '—';
  if (ms >= now) return `in ${relativeAge(new Date(now - (ms - now)), now)}`;
  return `${relativeAge(value, now)} ago`;
}

/** Full local timestamp for `title` attributes and detail headers. */
export function exactTime(value: string | Date | null | undefined): string {
  const ms = toMillis(value);
  if (ms === null) return '—';
  return new Date(ms).toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    timeZoneName: 'short',
  });
}

/** `HH:MM` in the given IANA zone (or the viewer's zone when omitted). */
export function timeOfDay(value: string | Date, timeZone?: string): string {
  const ms = toMillis(value);
  if (ms === null) return '—';
  return new Date(ms).toLocaleTimeString(undefined, {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    ...(timeZone ? { timeZone } : {}),
  });
}

export function shortDate(value: string | Date | null | undefined): string {
  const ms = toMillis(value);
  if (ms === null) return '—';
  return new Date(ms).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

/** Humanised duration from a number of seconds: `4m 12s`, `2h 05m`, `3d 4h`. */
export function formatDuration(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined || !Number.isFinite(seconds)) return '—';
  const s = Math.max(0, Math.round(seconds));
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m ${String(s % 60).padStart(2, '0')}s`;
  if (s < 86_400)
    return `${Math.floor(s / 3600)}h ${String(Math.floor((s % 3600) / 60)).padStart(2, '0')}m`;
  return `${Math.floor(s / 86_400)}d ${Math.floor((s % 86_400) / 3600)}h`;
}

/** Duration from minutes, used by escalation timeouts and snooze presets. */
export function formatMinutes(minutes: number): string {
  if (minutes < 60) return `${minutes} min`;
  if (minutes % 60 === 0) return `${minutes / 60} h`;
  return `${Math.floor(minutes / 60)} h ${minutes % 60} min`;
}

/** Thousands separated integer (tabular columns pair this with `.tnum`). */
export function formatCount(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '—';
  return value.toLocaleString();
}

/** Auto-compacted figure for stat tiles: 1,284 / 12.9K / 4.2M. */
export function compactNumber(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '—';
  const abs = Math.abs(value);
  if (abs < 10_000) return value.toLocaleString();
  if (abs < 1_000_000) return `${(value / 1000).toFixed(1).replace(/\.0$/, '')}K`;
  return `${(value / 1_000_000).toFixed(1).replace(/\.0$/, '')}M`;
}

/** Signed percentage delta, or null when the baseline is zero. */
export function percentDelta(current: number, previous: number): number | null {
  if (previous === 0) return current === 0 ? 0 : null;
  return ((current - previous) / previous) * 100;
}

export function formatPercentDelta(delta: number | null): string {
  if (delta === null) return 'new';
  const rounded = Math.abs(delta) >= 10 ? Math.round(delta) : Math.round(delta * 10) / 10;
  return `${rounded > 0 ? '+' : ''}${rounded}%`;
}

/** Initials for an avatar: "Ada Lovelace" -> "AL", "ops@x.com" -> "OP". */
export function initials(name: string | null | undefined): string {
  // An email is reduced to its local part first, so ops@example.com reads "OP".
  const source = (name ?? '').trim().split('@')[0]?.trim() ?? '';
  if (!source) return '??';
  const words = source.split(/[\s._-]+/).filter(Boolean);
  if (words.length >= 2) return `${words[0]![0]!}${words[1]![0]!}`.toUpperCase();
  return source.slice(0, 2).toUpperCase();
}

/**
 * Render any JSON value as text without ever producing "[object Object]":
 * objects and arrays are serialised, scalars are stringified, nullish is empty.
 */
export function safeString(value: unknown): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean' || typeof value === 'bigint') {
    return String(value);
  }
  try {
    return JSON.stringify(value) ?? '';
  } catch {
    return '[unserialisable]';
  }
}

const CHANNEL_NAMES: Record<string, string> = {
  sms: 'SMS',
  web_push: 'Push',
  servicedeskplus: 'ServiceDesk Plus',
  pagerduty: 'PagerDuty',
  servicenow: 'ServiceNow',
};

/** A delivery channel as people say it: `web_push` is "Push", `sms` is "SMS". */
export function channelLabel(channel: string): string {
  return CHANNEL_NAMES[channel] ?? humanise(channel);
}

/** Title-case a snake_case identifier for display (`m365_service_health`). */
export function humanise(value: string): string {
  return value
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/^./, (c) => c.toUpperCase());
}

function toMillis(value: string | Date | null | undefined): number | null {
  if (value === null || value === undefined) return null;
  const ms = value instanceof Date ? value.getTime() : Date.parse(value);
  return Number.isFinite(ms) ? ms : null;
}
